import { db } from '~/config/firebase.config';
import {
    doc,
    onSnapshot,
    runTransaction,
    serverTimestamp,
    setDoc,
} from 'firebase/firestore';

/**
 * Proxy durable du Disco Bingo. La CONSOLE est l'autorité Firestore (gotcha #75) :
 * le jeu (iframe) et le pavé animateur passent par ici, jamais en direct.
 *
 * État persistant par établissement, une partie à la fois :
 *   /clients/{clientID}/games/bingo/state/{roomID}
 *   { drawn: number[], maxNumber: 90|99, createdAt, updatedAt }
 *
 * `drawn` est ordonné (dernier = numéro en cours). Ne revient JAMAIS à []
 * sauf reset explicite → survit coupure/reload/plusieurs jours.
 */

/** Une seule « salle » de bingo par établissement, alignée sur `screenInfos.anim`. */
export const BINGO_ROOM_ID = 'anim';

export const ALLOWED_MAX = [90, 99];
export const DEFAULT_MAX = 90;

const normalizeMax = (m: number) => (ALLOWED_MAX.includes(m) ? m : DEFAULT_MAX);

export type BingoState = { drawn: number[]; maxNumber: number };

const stateRef = (clientID: string, roomID: string) =>
    doc(db, 'clients', clientID, 'games', 'bingo', 'state', roomID);

/** Normalise une valeur brute Firestore (garde anti-corruption). */
function readState(data: any): BingoState {
    const maxNumber = normalizeMax(Number(data?.maxNumber) || DEFAULT_MAX);
    const seen = new Set<number>();
    const drawn = Array.isArray(data?.drawn)
        ? data.drawn
              .map((n: any) => Math.trunc(Number(n)))
              .filter((n: number) => Number.isFinite(n) && n >= 1 && n <= maxNumber)
              .filter((n: number) => (seen.has(n) ? false : (seen.add(n), true)))
        : [];
    return { drawn, maxNumber };
}

/** Abonnement temps réel à l'état d'une partie. */
function subscribe(clientID: string, roomID: string) {
    return (onValue: (state: BingoState) => void) =>
        onSnapshot(stateRef(clientID, roomID), (snap) => {
            onValue(snap.exists() ? readState(snap.data()) : { drawn: [], maxNumber: DEFAULT_MAX });
        });
}

/**
 * Tire un numéro précis. Transaction : garde anti-doublon / hors-plage côté
 * serveur (l'animateur ET la TV peuvent écrire en même temps).
 */
async function draw(clientID: string, roomID: string, n: number): Promise<void> {
    const ref = stateRef(clientID, roomID);
    await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        const cur = snap.exists() ? readState(snap.data()) : { drawn: [], maxNumber: DEFAULT_MAX };
        if (!Number.isFinite(n) || n < 1 || n > cur.maxNumber) return;
        if (cur.drawn.includes(n)) return;
        const drawn = [...cur.drawn, n];
        if (snap.exists()) {
            tx.update(ref, { drawn, updatedAt: serverTimestamp() });
        } else {
            tx.set(ref, {
                drawn,
                maxNumber: cur.maxNumber,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
        }
    });
}

/** Annule le dernier numéro tiré. */
async function undo(clientID: string, roomID: string): Promise<void> {
    const ref = stateRef(clientID, roomID);
    await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) return;
        const cur = readState(snap.data());
        if (!cur.drawn.length) return;
        tx.update(ref, { drawn: cur.drawn.slice(0, -1), updatedAt: serverTimestamp() });
    });
}

/** Remet la partie à zéro. SEUL chemin qui vide `drawn` (préserve la plage). */
async function reset(clientID: string, roomID: string): Promise<void> {
    await setDoc(
        stateRef(clientID, roomID),
        { drawn: [], updatedAt: serverTimestamp() },
        { merge: true }
    );
}

/** Change la plage (90/99) en conservant les numéros encore valides. */
async function setMax(clientID: string, roomID: string, max: number): Promise<void> {
    // Une valeur invalide (NaN / hors liste) ne doit JAMAIS coercer vers 90 et
    // détruire des numéros déjà tirés (91-99) : on ignore plutôt que d'écraser.
    if (!ALLOWED_MAX.includes(max)) return;
    const maxNumber = max;
    const ref = stateRef(clientID, roomID);
    await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        const cur = snap.exists() ? readState(snap.data()) : { drawn: [], maxNumber: DEFAULT_MAX };
        const drawn = cur.drawn.filter((n) => n <= maxNumber);
        if (snap.exists()) {
            tx.update(ref, { maxNumber, drawn, updatedAt: serverTimestamp() });
        } else {
            tx.set(ref, {
                maxNumber,
                drawn,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
        }
    });
}

export const BingoAPI = {
    ref: stateRef,
    subscribe,
    draw,
    undo,
    reset,
    setMax,
    readState,
};
